// Third Party
// import { io } from "socket.io-client";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  Link,
  // useNavigate,
} from "@tanstack/react-router";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// Custom Hooks
import useUserToken from "@/hook/useUserToken";

// UI
import NotPermission from "@/components/NotPermission";
import { Card, CardContent } from "@/components/ui/card";
// import { Input } from "@/components/ui/input";
// import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

// Icon
// import { Plus } from "lucide-react";

// Utils
import { cn } from "@/lib/utils";
import {
  // CHAT_ENDPOINT,
  CHAT_BUCKET_LINK,
} from "@/components/constants";
import { getConversation } from "@/services/chat";
import { ConversationList } from "@/types/chat";
import calculatePassedTime from "@/utils/calculatePassedTime";

export const Route = createFileRoute("/_layout-private/chat/_layout")({
  component: RouteComponent,
});

function RouteComponent() {
  // Instance
  // const socket = io(CHAT_ENDPOINT);
  // const navigate = useNavigate();
  // const queryClient = useQueryClient();

  const user = useUserToken();
  const { chatMessages } = useAccessPermission();

  // Sockets
  // 2. Set up the user

  // Query
  const { data: conversationList, isLoading } = useQuery({
    queryKey: ["conversationList"],
    queryFn: () =>
      getConversation({ userId: user?.data?.userChatId, limit: 10, skip: 0 }),
    refetchInterval: 5000,
  });

  const conversations = conversationList?.data as ConversationList[];
  // console.log("Chat List", conversationList)

  return chatMessages.isRead ? (
    <section>
      <p className="text-[14px] mb-[32px]">ຂ້ໍຄວາມສົນທະນາ</p>
      <div className="gap-[20px] min-h-[calc(100vh-180px)] flex">
        <Card className="flex flex-col flex-1">
          {/* <CardHeader>
            <div className="flex items-center gap-4 mb-[16px]">
              <Input placeholder="ຄົ້ນຫາລັດວິສາຫະກິດ..." />
              {chatMessages.isWrite && (
                <Button
                  onClick={() =>
                    navigate({
                      to: "/chat/private/create",
                    })
                  }
                  variant={"outline"}
                  size={"icon"}
                >
                  <Plus />
                </Button>
              )}
            </div>
          </CardHeader> */}
          <CardContent>
            <p className="mt-3 text-sm font-medium text-gray-700">
              ລາຍຊື່ວິສາຫະກິດ
            </p>

            <div className="mt-4 h-[calc(100vh-410px)] space-y-2 overflow-y-auto">
              {isLoading ? (
                <ChatSkeleton />
              ) : (
                conversations?.map((item) => {
                  const receiver = item?.participants?.filter(
                    (item) => item?.user?._id !== user?.data?.userChatId
                  )[0];

                  // console.log(receiver, "receiver");
                  return (
                    <Link
                      to={
                        item?.conversationType === "PRIVATE"
                          ? "/chat/$id"
                          : "/chat/group/$id"
                      }
                      params={{ id: item._id }}
                      search={
                        item?.conversationType === "PRIVATE"
                          ? {
                              receiverId: receiver?.user?._id,
                              profileUrl: receiver?.user?.profileUrl,
                              fullName: receiver?.user?.fullName,
                              phone: receiver?.user?.phone,
                            }
                          : {
                              conversationImage: item?.conversationImage,
                              conversationName: item?.conversationName,
                              participants: String(item?.participants?.length),
                            }
                      }
                      // className="flex items-center gap-[28px] p-2 relative"
                      className={cn(
                        "flex items-center gap-[28px]   p-2 relative",
                        item?.latestMessage?.isRead ||
                          item?.latestMessage?.sender?._id ===
                            user?.data?.userChatId
                          ? ""
                          : "bg-primary/5 rounded-md relative"
                      )}
                      key={item._id}
                    >
                      {item?.latestMessage?.isRead ||
                      item?.latestMessage?.sender?._id ===
                        user?.data?.userChatId ? null : (
                        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary" />
                      )}

                      <Avatar className="w-[48px] h-[48px]">
                        <AvatarImage
                          src={`${CHAT_BUCKET_LINK}/images/${
                            item?.conversationType === "PRIVATE"
                              ? receiver?.user?.profileUrl
                              : item?.conversationImage
                          }`}
                          className="object-cover"
                        />
                        <AvatarFallback>
                          {item?.conversationType === "PRIVATE"
                            ? receiver?.user?.fullName?.charAt(0)
                            : item?.conversationName?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>

                      <div>
                        <h3 className="text-sm">
                          {item?.conversationType === "PRIVATE"
                            ? receiver?.user?.fullName
                            : item?.conversationName}
                        </h3>
                        <p
                          className={cn(
                            "text-sm",
                            item?.latestMessage?.isRead ||
                              item?.latestMessage?.sender?._id ===
                                user?.data?.userChatId
                              ? ""
                              : ""
                          )}
                        >
                          {item?.latestMessageData?.content?.length > 45
                            ? `${item?.latestMessageData?.content.slice(
                                0,
                                45
                              )}...`
                            : item?.latestMessageData?.content}
                        </p>
                      </div>

                      <small className="absolute right-2 bottom-2">
                        {calculatePassedTime(item?.latestMessageData?.sendAt)}
                      </small>
                    </Link>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="w-[65%]">
          <CardContent className="p-0">
            <Outlet />
          </CardContent>
        </Card>
      </div>
    </section>
  ) : (
    <NotPermission />
  );
}

const ChatSkeleton = () => {
  return (
    <div className="flex flex-col gap-4 p-4">
      {[1, 2, 3, 4, 5].map((index) => (
        <div key={index} className="flex items-center space-x-4">
          <Skeleton className="w-12 h-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-[200px]" />
            <Skeleton className="h-4 w-[300px]" />
          </div>
          <div className="flex flex-col items-end space-y-2">
            <Skeleton className="h-4 w-[60px]" />
            <Skeleton className="w-5 h-5 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
};
